<script setup lang="ts">
import VButton from "@verbleif/lib/src/components/Button/VButton.vue";
import DialogFooter from "@verbleif/lib/src/components/Dialog/DialogFooter.vue";
import VTag from "@verbleif/lib/src/components/Tag/VTag.vue";
import { confirm } from "@verbleif/lib/src/composables/Dialog/confirm";
import { computed, inject } from "vue";
import { useI18n } from "vue-i18n";
import DebugJson from "../parts/DebugJson.vue";
import { DEBUG_TOOLS_CONTEXT } from "../types";
import { useEnvironmentForm } from "./useEnvironmentForm";

const context = inject(DEBUG_TOOLS_CONTEXT, null);
if (context === null) {
  throw new Error("The Omgeving footer only works inside the debug tools dialog.");
}

const { t } = useI18n();
const form = useEnvironmentForm();

const canRestorePreset = computed(() => form.basePreset.value !== null
  && (form.dirty.value || form.state.value.kind === "custom"));

function showFormDebug() {
  void confirm({
    title: t("debug_tools.environment.form_debug"),
    body: DebugJson,
    bodyProps: {
      value: {
        values: { ...form.values },
        changed: form.changedKeys.value,
        errors: form.errors.value,
        state: form.state.value,
        basePreset: form.basePreset.value,
        override: context?.config.overrideConfig.value ?? null,
      },
    },
    confirmText: t("base.close"),
    hideCancel: true,
    variant: "info",
    size: "large",
  });
}
</script>

<template>
  <DialogFooter>
    <div class="flex flex-wrap items-center gap-2">
      <VButton
        variant="danger"
        outlined
        rounded
        icon-left="undo"
        :disabled="!form.hasOverride.value"
        data-testid="debug-environment-reset"
        @click="form.resetToDefault()"
      >
        {{ t("debug_tools.environment.reset_default") }}
      </VButton>
      <VButton
        v-if="canRestorePreset && form.basePreset.value !== null"
        variant="gray"
        rounded
        icon-left="undo"
        data-testid="debug-environment-restore-preset"
        @click="form.resetToPreset()"
      >
        {{ t("debug_tools.environment.restore_preset", { preset: form.presetLabel(form.basePreset.value) }) }}
      </VButton>
    </div>
    <div class="flex flex-wrap items-center justify-end gap-2">
      <VTag
        v-if="form.dirty.value"
        :text="t('debug_tools.environment.unsaved')"
        variant="is-warning"
        icon-less
        data-testid="debug-environment-unsaved"
      />
      <VButton
        v-if="context.config.debugMode.value"
        variant="light"
        rounded
        data-testid="debug-environment-form-debug"
        @click="showFormDebug"
      >
        {{ t("debug_tools.environment.form_debug") }}
      </VButton>
      <VButton variant="gray" rounded data-testid="debug-tools-close" @click="context.close()">
        {{ t("base.close") }}
      </VButton>
      <VButton
        variant="primary"
        rounded
        icon-left="check"
        :disabled="!form.dirty.value"
        data-testid="debug-environment-save"
        @click="form.save()"
      >
        {{ t("base.save") }}
      </VButton>
    </div>
  </DialogFooter>
</template>
